import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchAtencionBotProductos } from '../../api/atencionBot';
import { confirmCatalogExit } from '../../utils/catalogExitConfirm';

function formatCountdown(seconds) {
  const safe = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function useCatalogCountdown(expiresInSeconds, onExpired) {
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Number(expiresInSeconds) || 0)
  );
  const expiredNotified = useRef(false);

  useEffect(() => {
    const initial = Math.max(0, Number(expiresInSeconds) || 0);
    if (!initial) {
      setRemaining(0);
      return undefined;
    }
    const expiresAt = Date.now() + initial * 1000;
    const tick = () => {
      const left = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0 && !expiredNotified.current) {
        expiredNotified.current = true;
        onExpired?.();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresInSeconds, onExpired]);

  return remaining;
}

function formatPrice(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return 'consultar';
  return num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function productKey(item) {
  return String(item?.idproducto || item?.producto || '');
}

function normalizeProduct(item) {
  if (!item) return null;
  const isActive = Number(item.is_active ?? item.isActive ?? 0) === 1;
  const basePromo = Number(item.precio_sin_descuento ?? item.precioSinDescuento ?? 0);
  const finalPromo = Number(
    item.precio_con_descuento ?? item.precio_descuento ?? item.precioConDescuento ?? 0
  );
  return {
    idproducto: item.idproducto ?? item.codplex ?? null,
    producto: item.producto || 'Sin nombre',
    presentacion: item.presentacion || '',
    droganombre: item.droganombre || item.droga || null,
    precio: item.precio ?? null,
    stock_q: item.stock_q ?? item.stock ?? null,
    precio_sin_descuento: Number.isFinite(basePromo) && basePromo > 0 ? basePromo : null,
    precio_con_descuento: Number.isFinite(finalPromo) && finalPromo > 0 ? finalPromo : null,
    precio_descuento: Number.isFinite(finalPromo) && finalPromo > 0 ? finalPromo : null,
    dto_peuma: item.dto_peuma ?? item.dtoPeuma ?? '',
    is_active: isActive ? 1 : 0,
    purchase_count: item.purchase_count ?? null,
    is_cronico: Boolean(item.is_cronico || item.isCronico),
    source: item.source || 'farmachat'
  };
}

function formatPromoLine(product) {
  const active = Number(product.is_active || 0) === 1;
  const base = Number(product.precio_sin_descuento || 0);
  const final = Number(product.precio_con_descuento || product.precio_descuento || 0);
  if (!active || !Number.isFinite(base) || !Number.isFinite(final) || base <= 0 || final <= 0) {
    return null;
  }
  const dto = String(product.dto_peuma || '').trim();
  const dtoLabel = dto ? (dto.endsWith('%') ? dto : `${dto}%`) : '';
  return {
    base: formatPrice(base),
    final: formatPrice(final),
    dto: dtoLabel
  };
}

function FrequentProductCard({ product, rank, selected, onToggle }) {
  const promo = formatPromoLine(product);
  return (
    <button
      type="button"
      className={`crm-bf-catalog-frequent-card${selected ? ' is-selected' : ''}${
        product.is_cronico ? ' is-chronic' : ''
      }`}
      onClick={() => onToggle(product)}
      aria-pressed={selected}
    >
      <span className="crm-bf-catalog-frequent-card__rank" aria-hidden="true">
        {rank}
      </span>
      <span className="crm-bf-catalog-frequent-card__check" aria-hidden="true">
        {selected ? '✓' : ''}
      </span>
      <span className="crm-bf-catalog-frequent-card__name">{product.producto}</span>
      {product.presentacion ? (
        <span className="crm-bf-catalog-frequent-card__pres">{product.presentacion}</span>
      ) : null}
      {product.droganombre ? (
        <span className="crm-bf-catalog-frequent-card__droga">{product.droganombre}</span>
      ) : null}
      {promo ? (
        <span className="crm-bf-catalog-frequent-card__promo">
          <span className="crm-bf-catalog-frequent-card__promo-base">~${promo.base}~</span>
          {promo.dto ? (
            <span className="crm-bf-catalog-frequent-card__promo-dto">
              {promo.dto}
            </span>
          ) : null}
          <strong>${promo.final}</strong>
        </span>
      ) : null}
      {selected ? (
        <span className="crm-bf-catalog-frequent-card__added">
          <span aria-hidden="true">✓</span> Agregado
        </span>
      ) : (
        <span className="crm-bf-catalog-frequent-card__cta">Elegir</span>
      )}
      <span className="crm-bf-catalog-frequent-card__foot">
        <strong>${formatPrice(product.precio)}</strong>
        {product.stock_q != null ? (
          <span>Stock {Number(product.stock_q).toLocaleString('es-AR')} u.</span>
        ) : null}
      </span>
    </button>
  );
}

function CatalogProductCard({ product, selected, onToggle, showPurchaseCount = false }) {
  const key = productKey(product);
  const promo = formatPromoLine(product);
  return (
    <button
      key={key}
      type="button"
      className={`crm-bf-catalog-card${selected ? ' is-selected' : ''}`}
      onClick={() => onToggle(product)}
      aria-pressed={selected}
    >
      {/* {showPurchaseCount && product.purchase_count != null && !selected ? (
        <span className="crm-bf-catalog-card__freq" title="Veces en compras farmacia">
          {product.purchase_count}× compras
        </span>
      ) : null} */}
      <span className="crm-bf-catalog-card__check" aria-hidden="true">
        {selected ? '✓' : ''}
      </span>
      <span className="crm-bf-catalog-card__name">{product.producto}</span>
      {product.presentacion ? (
        <span className="crm-bf-catalog-card__pres">{product.presentacion}</span>
      ) : null}
      {product.droganombre ? (
        <span className="crm-bf-catalog-card__droga">{product.droganombre}</span>
      ) : null}
      {promo ? (
        <span className="crm-bf-catalog-card__promo">
          <span className="crm-bf-catalog-card__promo-base">~${promo.base}~</span>
          {promo.dto ? (
            <span className="crm-bf-catalog-card__promo-dto">{promo.dto}</span>
          ) : null}
          <strong>${promo.final}</strong>
        </span>
      ) : null}
      {selected ? (
        <span className="crm-bf-catalog-card__added">
          <span aria-hidden="true">✓</span> Agregado
        </span>
      ) : null}
      <span className="crm-bf-catalog-card__foot">
        {!promo ? (
          <strong>${formatPrice(product.precio)}</strong>
        ) : null}
        {product.stock_q != null ? <span>Stock {Number(product.stock_q).toLocaleString('es-AR')} u.</span> : null}
      </span>
    </button>
  );
}

export default function BotFarmaciaCatalogPanel({
  initialHits = [],
  initialFrequent = [],
  frequentEnriching = false,
  initialQuery = '',
  contactLabel = '',
  expiresInSeconds = null,
  onSessionExpired,
  onClose,
  onAddToCart,
  adding = false
}) {
  const handleSessionExpired = useCallback(() => {
    onSessionExpired?.();
  }, [onSessionExpired]);

  const countdownSeconds = useCatalogCountdown(expiresInSeconds, handleSessionExpired);
  const showCountdown = Number(expiresInSeconds) > 0;
  const countdownUrgent = showCountdown && countdownSeconds > 0 && countdownSeconds <= 300;
  const countdownCritical = showCountdown && countdownSeconds > 0 && countdownSeconds <= 60;
  const sessionExpired = showCountdown && countdownSeconds <= 0;
  const seedProducts = useMemo(
    () => (Array.isArray(initialHits) ? initialHits.map(normalizeProduct).filter(Boolean) : []),
    [initialHits]
  );

  const frequentProducts = useMemo(
    () =>
      Array.isArray(initialFrequent) ? initialFrequent.map(normalizeProduct).filter(Boolean) : [],
    [initialFrequent]
  );

  const [resolvedFrequent, setResolvedFrequent] = useState(frequentProducts);
  const [frequentPriceLoading, setFrequentPriceLoading] = useState(false);
  const frequentClientEnrichRef = useRef('');

  useEffect(() => {
    setResolvedFrequent(frequentProducts);
    frequentClientEnrichRef.current = '';
  }, [frequentProducts]);

  useEffect(() => {
    if (frequentEnriching || !frequentProducts.length) return undefined;

    const enrichKey = frequentProducts.map((p) => productKey(p)).join('|');
    if (frequentClientEnrichRef.current === enrichKey) return undefined;

    const missing = frequentProducts.filter((p) => {
      const price = Number(p.precio);
      return !Number.isFinite(price) || price <= 0;
    });
    if (!missing.length) {
      frequentClientEnrichRef.current = enrichKey;
      return undefined;
    }

    let cancelled = false;
    setFrequentPriceLoading(true);

    void (async () => {
      try {
        const enriched = await Promise.all(
          frequentProducts.map(async (product) => {
            const price = Number(product.precio);
            if (Number.isFinite(price) && price > 0) return product;
            try {
              const data = await fetchAtencionBotProductos(product.producto, { limit: 3 });
              const hits = Array.isArray(data?.productos) ? data.productos : [];
              const label = String(product.producto || '').toLowerCase();
              const hit =
                hits.find((row) => String(row?.producto || '').toLowerCase().includes(label)) ||
                hits[0];
              if (!hit) return product;
              const normalized = normalizeProduct(hit);
              return {
                ...product,
                ...normalized,
                purchase_count: product.purchase_count,
                is_cronico: product.is_cronico
              };
            } catch {
              return product;
            }
          })
        );
        if (!cancelled) {
          frequentClientEnrichRef.current = enrichKey;
          setResolvedFrequent(enriched);
        }
      } finally {
        if (!cancelled) setFrequentPriceLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [frequentEnriching, frequentProducts]);

  const [search, setSearch] = useState(initialQuery || '');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cart, setCart] = useState([]);
  const [frequentFilter, setFrequentFilter] = useState('');

  const cartKeys = useMemo(() => new Set(cart.map(productKey)), [cart]);

  const filteredFrequent = useMemo(() => {
    const q = String(frequentFilter || '').trim().toLowerCase();
    if (!q) return resolvedFrequent;
    return resolvedFrequent.filter((p) => {
      const haystack = [p.producto, p.presentacion, p.droganombre].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [frequentFilter, resolvedFrequent]);

  const runSearch = useCallback(async (query) => {
    const q = String(query || '').trim();
    setAppliedSearch(q);
    if (!q) {
      setResults([]);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchAtencionBotProductos(q, { limit: 24 });
      const list = Array.isArray(data?.productos) ? data.productos.map(normalizeProduct).filter(Boolean) : [];
      if (!data?.configured) {
        setError('API Farmachat no configurada en el bot.');
      }
      setResults(list);
      if (!list.length && data?.configured) {
        setError('No encontramos productos con ese criterio.');
      }
    } catch (err) {
      setResults([]);
      setError(err?.message || 'No se pudo buscar productos.');
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleCart = (product) => {
    if (sessionExpired) return;
    const key = productKey(product);
    setCart((prev) => {
      const exists = prev.some((p) => productKey(p) === key);
      if (exists) return prev.filter((p) => productKey(p) !== key);
      return [...prev, product];
    });
  };

  const handleSubmitSearch = (e) => {
    e?.preventDefault?.();
    runSearch(search);
  };

  const handleAddToCart = () => {
    if (!cart.length || adding || sessionExpired) return;
    onAddToCart?.(cart);
  };

  const requestClose = useCallback(async () => {
    const result = await confirmCatalogExit({ cartCount: cart.length });
    if (result.isConfirmed) {
      onClose?.();
    }
  }, [cart.length, onClose]);

  const chatOnlySeeds = useMemo(() => {
    if (!seedProducts.length) return [];
    const frequentKeys = new Set(resolvedFrequent.map(productKey));
    return seedProducts.filter((p) => !frequentKeys.has(productKey(p)));
  }, [resolvedFrequent, seedProducts]);

  const showFrequent =
    !appliedSearch && (resolvedFrequent.length > 0 || frequentEnriching || frequentPriceLoading);
  const showChatHits = !appliedSearch && chatOnlySeeds.length > 0;
  const displayList = appliedSearch ? results : [];

  return (
    <div className="crm-bf-catalog">
      <header className="crm-bf-catalog__head">
        <div className="crm-bf-catalog__head-accent" aria-hidden="true" />
        <div className="crm-bf-catalog__head-row">
          <div className="crm-bf-catalog__head-copy">
            <p className="crm-bf-catalog__eyebrow">Catálogo Farmachat · simulación</p>
            {showCountdown ? (
              <div
                className={`crm-bf-catalog__timer${
                  sessionExpired
                    ? ' crm-bf-catalog__timer--expired'
                    : countdownCritical
                      ? ' crm-bf-catalog__timer--critical'
                      : countdownUrgent
                        ? ' crm-bf-catalog__timer--warn'
                        : ''
                }`}
                role="timer"
                aria-live="polite"
                aria-atomic="true"
                aria-label={
                  sessionExpired
                    ? 'Enlace vencido'
                    : `Tiempo restante del enlace: ${formatCountdown(countdownSeconds)}`
                }
              >
                <span className="crm-bf-catalog__timer-icon" aria-hidden="true">
                  {sessionExpired ? '⏱' : '⏳'}
                </span>
                <span className="crm-bf-catalog__timer-label">
                  {sessionExpired ? 'Enlace vencido' : 'Tu enlace vence en'}
                </span>
                {!sessionExpired ? (
                  <strong className="crm-bf-catalog__timer-value">
                    {formatCountdown(countdownSeconds)}
                  </strong>
                ) : null}
              </div>
            ) : null}
            <h1 className="crm-bf-catalog__title">Elegí productos</h1>
            <p className="crm-bf-catalog__sub">
              {sessionExpired
                ? 'El tiempo del catálogo terminó. Volvé al chat y pedile a Anita un nuevo enlace.'
                : contactLabel
                  ? `Para ${contactLabel} — seleccioná y volvé al chat con «Agregar al carrito».`
                  : 'Buscá, seleccioná y volvé al chat con «Agregar al carrito».'}
            </p>
          </div>
          <button
            type="button"
            className="crm-bf-catalog__back"
            onClick={requestClose}
            aria-label="Volver al chat"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z"
              />
            </svg>
          </button>
        </div>

        <form className="crm-bf-catalog__search" onSubmit={handleSubmitSearch}>
          <span className="crm-bf-catalog__search-icon" aria-hidden="true">
            🔍
          </span>
          <input
            type="search"
            className="crm-bf-catalog__search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar medicamento, droga, marca…"
          />
          <button type="submit" className="crm-bf-btn crm-bf-btn--primary" disabled={loading}>
            {loading ? 'Buscando…' : 'Buscar'}
          </button>
        </form>
      </header>

      <div className="crm-bf-catalog__body">
        {showFrequent ? (
          <section
            className="crm-bf-catalog-frequent crm-bf-catalog-frequent--premium"
            aria-label="Los que más llevás"
          >
            <div className="crm-bf-catalog-frequent__hero">
              <span className="crm-bf-catalog-frequent__hero-icon" aria-hidden="true">
                ⭐
              </span>
              <div className="crm-bf-catalog-frequent__hero-copy">
                <p className="crm-bf-catalog-frequent__heading">Los que más llevás</p>
                <p className="crm-bf-catalog-frequent__hero-sub">Tus habituales en farmacia</p>
              </div>
            </div>

            <div className="crm-bf-catalog-frequent__toolbar">
              <span className="crm-bf-catalog-frequent__toolbar-icon" aria-hidden="true">
                🔍
              </span>
              <input
                type="search"
                className="crm-bf-catalog-frequent__toolbar-input"
                placeholder="Filtrar tus habituales…"
                value={frequentFilter}
                onChange={(e) => setFrequentFilter(e.target.value)}
                aria-label="Filtrar medicamentos frecuentes"
              />
              {frequentFilter ? (
                <button
                  type="button"
                  className="crm-bf-catalog-frequent__toolbar-clear"
                  onClick={() => setFrequentFilter('')}
                  aria-label="Limpiar filtro"
                >
                  ×
                </button>
              ) : null}
            </div>

            <p className="crm-bf-catalog-frequent__meta">
              {frequentEnriching || frequentPriceLoading ? (
                <span className="crm-bf-catalog-frequent__enriching">
                  <span className="crm-bf-catalog-public-spinner crm-bf-catalog-public-spinner--inline" aria-hidden="true" />
                  {resolvedFrequent.length
                    ? 'Actualizando precios y stock…'
                    : 'Cargando tus productos habituales…'}
                </span>
              ) : (
                <>
                  {filteredFrequent.length} medicamento{filteredFrequent.length === 1 ? '' : 's'} · ordenados por
                  compras
                </>
              )}
            </p>

            {filteredFrequent.length === 0 && frequentEnriching ? (
              <div className="crm-bf-catalog-frequent__grid" aria-hidden="true">
                {[1, 2, 3, 4].map((n) => (
                  <div
                    key={n}
                    className="crm-bf-catalog-frequent-card crm-bf-catalog-frequent-card--skeleton"
                  />
                ))}
              </div>
            ) : filteredFrequent.length === 0 ? (
              <div className="crm-bf-catalog-frequent__empty">
                <span aria-hidden="true">🔎</span>
                <p>Sin coincidencias para tu búsqueda.</p>
              </div>
            ) : (
              <div className="crm-bf-catalog-frequent__grid">
                {filteredFrequent.map((product, index) => (
                  <FrequentProductCard
                    key={productKey(product)}
                    product={product}
                    rank={index + 1}
                    selected={cartKeys.has(productKey(product))}
                    onToggle={toggleCart}
                  />
                ))}
              </div>
            )}
          </section>
        ) : null}

        {showChatHits ? (
          <>
            <p className="crm-bf-catalog__section-label">Opciones que Anita mostró en el chat</p>
            <div className="crm-bf-catalog__grid">
              {chatOnlySeeds.map((product) => (
                <CatalogProductCard
                  key={productKey(product)}
                  product={product}
                  selected={cartKeys.has(productKey(product))}
                  onToggle={toggleCart}
                />
              ))}
            </div>
          </>
        ) : null}

        {error ? (
          <div className="crm-bf-catalog__state crm-bf-catalog__state--error" role="alert">
            {error}
          </div>
        ) : null}

        {!loading && appliedSearch && !displayList.length && !error ? (
          <div className="crm-bf-catalog__state">
            <span aria-hidden="true">📦</span>
            <p>No encontramos productos con ese criterio.</p>
          </div>
        ) : null}

        {!showFrequent && !showChatHits && !appliedSearch && !loading ? (
          <div className="crm-bf-catalog__state">
            <span aria-hidden="true">📦</span>
            <p>Buscá un producto para ver resultados del catálogo.</p>
          </div>
        ) : null}

        {loading ? (
          <div className="crm-bf-catalog__state crm-bf-catalog__state--inline-load" role="status" aria-busy="true">
            <div className="crm-bf-catalog-public-spinner" aria-hidden="true" />
            <p>Consultando API Farmachat…</p>
          </div>
        ) : appliedSearch && displayList.length ? (
          <div className="crm-bf-catalog__grid">
            {displayList.map((product) => (
              <CatalogProductCard
                key={productKey(product)}
                product={product}
                selected={cartKeys.has(productKey(product))}
                onToggle={toggleCart}
              />
            ))}
          </div>
        ) : null}
      </div>

      <footer className="crm-bf-catalog__foot">
        <div className="crm-bf-catalog__cart-summary">
          <span className="crm-bf-catalog__cart-count">
            <strong>{cart.length}</strong> en carrito
          </span>
          {cart.length ? (
            <span className="crm-bf-catalog__cart-preview">
              {cart.map((p) => p.producto).join(' · ')}
            </span>
          ) : (
            <span className="crm-bf-catalog__cart-preview crm-bf-catalog__cart-preview--empty">
              Tocá un producto para seleccionarlo
            </span>
          )}
        </div>
        <div className="crm-bf-catalog__foot-actions">
          <button type="button" className="crm-bf-btn crm-bf-btn--ghost" onClick={requestClose}>
            Volver al chat
          </button>
          <button
            type="button"
            className="crm-bf-btn crm-bf-btn--primary crm-bf-catalog__add-btn"
            onClick={handleAddToCart}
            disabled={!cart.length || adding || sessionExpired}
          >
            {sessionExpired ? 'Enlace vencido' : adding ? 'Agregando…' : '🛒 Agregar al carrito'}
          </button>
        </div>
      </footer>

      {adding ? (
        <div
          className="crm-bf-catalog-checkout-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="crm-bf-catalog-checkout-overlay__card">
            <div className="crm-bf-catalog-public-spinner" aria-hidden="true" />
            <p className="crm-bf-catalog-checkout-overlay__title">Agregando al carrito…</p>
            <p className="crm-bf-catalog-checkout-overlay__hint">Volviendo al chat con Anita</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
